/* eslint-disable */
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useOutletContext } from "react-router-dom";
import useHttp from "../../hooks/use-http";
import Spinner from "../Spinner";
import { generateQR } from "paytent-qr";
import toast from "react-hot-toast";
import { notifyError, notifyRedirecting } from "../../utils/embedHelper";

const QrPayment = () => {
  const outletContext = useOutletContext() || {};
  const { transactionInfo } = outletContext;
  const [qrData, setQrData] = useState({});
  const [time, setTime] = useState("--:--");
  const [isExpired, setIsExpired] = useState(false);
  const [url, setUrl] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const { sendRequest: fetchQrInfo, isLoading: qrLoading, error } = useHttp();
  const { sendRequest: getReturnUrl } = useHttp();
  const [qrStatusLoading, setQrStatusLoading] = useState(false);
  const [qrCodeUrl, setQrCodeUrl] = useState("");

  const { id } = useParams();
  const navigate = useNavigate();
  const qrCalled = useRef(false);

  const isTestMode =
    transactionInfo?.data &&
    (transactionInfo.data.isLive === false || transactionInfo.data.isLive === "false");

  const confirmCancel = (data) => {
    setUrl(data?.data?.returnUrl);
  };

  /* const cancelTransaction = useCallback(async () => {
    getReturnUrl(
      {
        url: `${process.env.REACT_APP_BASE_URL}/api/transaction/cancelTransaction/${id}`,
        headers: { "Content-Type": "application/json" },
      },
      confirmCancel,
    );
  }, [getReturnUrl, id]);

  useEffect(() => {
    cancelTransaction();
  }, []); */

  const getQrDetails = useCallback((data) => {
    if (!data?.data?.code) {
      setErrorMsg("Failed to generate QR. Please try again later.");
      return;
    }
    setQrData(data);
  }, []);

  const fetchQrDetails = useCallback(async () => {
    fetchQrInfo(
      {
        url: `${process.env.REACT_APP_BASE_URL}/api/Qr/Generate/${id}`,
        headers: {
          "Content-Type": "application/json",
        },
      },
      getQrDetails,
    );
  }, [fetchQrInfo, id, getQrDetails]);

  useEffect(() => {
    if (qrCalled.current) return;
    qrCalled.current = true;
    fetchQrDetails();
  }, [fetchQrDetails]);

  const getQrStatus = useCallback((data) => {
    // 1. Terminal / Expiration check:
    if (data?.data?.status?.toLowerCase() === "expired" || data?.message?.toLowerCase() === "qrcode expired") {
      const returnUrl = data?.data?.returnUrl;
      const msg = data?.message || "QR Code has expired. Redirecting you back...";

      toast.error(msg);
      setErrorMsg(msg);
      setIsExpired(true);

      if (returnUrl) {
        setTimeout(() => {
          notifyRedirecting({ returnUrl, status: "expired", reference: id });
          notifyError({ returnUrl, status: "expired", message: msg, reference: id });
          window.location.href = returnUrl;
        }, 3000);
      }
      return;
    }

    // 2. Pending / Not yet paid check:
    if (
      data?.success === false ||
      data?.message?.toLowerCase() === "initiated" ||
      data?.message?.toLowerCase() === "payment not yet received"
    ) {
      toast.error(data?.message || "Payment not yet received. Please try again after completing the transfer.");
      return;
    }

    // 3. Success check:
    toast.success("Payment confirmed successfully!");
    navigate(`/qr/${id}/payment-confirmation/${id}`);
  }, [navigate, id]);

  const fetchQrStatus = useCallback(async () => {
    if (!qrData.data?.id) return;
    setQrStatusLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}api/Qr/ResolveExpiredStatus/${id}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
      const data = await response.json();
      getQrStatus(data);
    } catch (err) {
      toast.error(err.message || "Failed to check status");
    } finally {
      setQrStatusLoading(false);
    }
  }, [qrData.data?.id, getQrStatus, id]);

  const fetchQrStatusMain = useCallback(async () => {
    if (!qrData.data?.id) return;
    setQrStatusLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/api/Qr/Status/${qrData.data?.id}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
      const data = await response.json();
      getQrStatus(data);
    } catch (err) {
      toast.error(err.message || "Failed to check status");
    } finally {
      setQrStatusLoading(false);
    }
  }, [qrData.data?.id, getQrStatus]);



  const convertStringToTime = (timeString) => {
    if (!timeString) return null;
    const [timeVal, period] = timeString.split(" ");
    if (!timeVal || !period) return null;

    const [hours, minutes] = timeVal.split(":");
    if (!hours || !minutes) return null;

    let newHours = parseInt(hours);
    if (period === "PM" && newHours !== 12) {
      newHours += 12;
    }

    const timeObject = new Date();
    timeObject.setHours(newHours, parseInt(minutes), 0, 0);
    return timeObject;
  };

  const calculateTimeDifference = useCallback(() => {
    const currentTime = new Date();
    const expirationTime = convertStringToTime(qrData?.data?.time);

    if (expirationTime) {
      const timeDiff = expirationTime - currentTime;
      if (timeDiff > 0) {
        setTime(formatTime(timeDiff));
      } else {
        setTime("00:00");
        setIsExpired(true);
      }
    }
  }, [qrData?.data?.time]);

  const formatTime = (timeInMilliseconds) => {
    const minutes = Math.floor(timeInMilliseconds / (1000 * 60));
    const seconds = Math.floor((timeInMilliseconds % (1000 * 60)) / 1000);
    return `${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
  };

  const hasFetchedExpiry = useRef(false);

  useEffect(() => {
    if (isExpired && !hasFetchedExpiry.current) {
      hasFetchedExpiry.current = true;
      fetchQrStatus();
    }
  }, [isExpired, fetchQrStatus]);

  useEffect(() => {
    calculateTimeDifference();
    const interval = setInterval(() => {
      calculateTimeDifference();
    }, 1000);
    return () => clearInterval(interval);
  }, [calculateTimeDifference]);

  useEffect(() => {
    if (qrData.data?.code) {
      generateQR(qrData.data?.code, { width: 220 }).then(url => {
        if (url) {
          setQrCodeUrl(url);
        }
      });
    }
  }, [qrData.data?.code]);

  const currentCurrencySymbol = transactionInfo?.data?.currency === "NGN" ? "₦" : (transactionInfo?.data?.currency || "NGN");
  const totalAmountValue = (transactionInfo?.data?.amount || 0) + (transactionInfo?.data?.fee || 0);

  return (
    <div className="w-full max-w-[560px] mx-auto py-2 px-4 sm:px-0">
      {/* Test Transaction Ribbon */}
      {isTestMode && (
        <div
          id="test-transaction-ribbon"
          data-testid="test-transaction-ribbon"
          className="w-full mb-5"
        >
          <div className="w-full bg-gradient-to-r from-red-600 via-rose-600 to-red-600 text-white text-xs sm:text-sm font-bold py-2.5 px-4 rounded-lg shadow-sm border border-red-700/30 flex items-center justify-center gap-2 uppercase tracking-wider text-center">
            <svg
              className="w-4 h-4 flex-shrink-0 text-white"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth="2.5"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
            <span>TEST TRANSACTION</span>
          </div>
        </div>
      )}

      <div className="text-center px-4 mb-5">
        <p className="text-slate-600 text-sm leading-relaxed max-w-[440px] mx-auto">
          Scan the QR code below using your phone’s camera, the Paytent App, or any QR scanner app to proceed with payment.
        </p>
      </div>

      <div className="space-y-6">
        {/* QR Code Container */}
        <div className="flex flex-col items-center justify-center p-4 bg-[#00aeef] rounded-2xl w-fit mx-auto shadow-sm">
          {qrLoading && (
            <div className="w-[220px] h-[220px] flex items-center justify-center bg-white rounded-xl">
              <Spinner />
            </div>
          )}

          {(error || errorMsg) && !qrLoading && (
            <div className="w-[220px] h-[220px] flex items-center justify-center bg-white rounded-xl">
              <p className="text-red-500 text-xs font-semibold px-4 text-center">
                Failed to generate QR. Please try again.
              </p>
            </div>
          )}

          <div className={`rounded-xl overflow-hidden bg-white p-3.5 ${(qrLoading || error || errorMsg) ? "hidden" : ""}`}>
            <div className="w-[220px] h-[220px] flex items-center justify-center">
              {qrCodeUrl ? (
                <a
                  href={qrData.data?.code}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="cursor-pointer block active:scale-[0.98] transition-transform"
                >
                  <img
                    src={qrCodeUrl}
                    alt="QR Code"
                    width={220}
                    height={220}
                    className="mx-auto"
                  />
                </a>
              ) : (
                <Spinner />
              )}
            </div>
          </div>
        </div>

        {/* Timers & Helper Info */}
        <div className="text-center space-y-2">
          <p className="text-[11px] text-[#00aeef] font-semibold md:hidden bg-sky-50 py-1.5 px-3 rounded-lg border border-sky-100/50 w-fit mx-auto animate-pulse">
            💡 If on mobile, you can click the QR code to open the payment link directly.
          </p>
          <p className="text-xs text-slate-500 max-w-[420px] mx-auto leading-relaxed">
            Complete payment, then click <strong className="text-slate-700">"I've Paid {currentCurrencySymbol}{totalAmountValue.toLocaleString()}"</strong> to verify.
          </p>
          <p className="text-xs font-semibold text-black font-mono">
            This QR code expires in <span className="font-bold text-red-500 bg-red-50 px-2 py-0.5 rounded border border-red-100">{time}</span>
          </p>
        </div>

        {/* Action Button */}
        <div className="pt-2">
          <button
            type="button"
            className="w-full h-[54px] bg-[#00aeef] hover:bg-[#0096ce] active:scale-[0.99] text-white rounded-lg font-bold text-base cursor-pointer transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
            onClick={fetchQrStatusMain}
            disabled={qrStatusLoading}
          >
            {qrStatusLoading ? (
              <Spinner />
            ) : (
              `I’ve Paid ${currentCurrencySymbol}${totalAmountValue.toLocaleString()}`
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default QrPayment;